/**
 * @glass/types — the wire shapes both UIs speak (BUILD.md Appendix B, verbatim where possible).
 *
 * Plain TypeScript, no runtime dependencies, no react-native import: `apps/ui` (RN + web),
 * `apps/review` (Vite) and their test runners all consume this source directly.
 *
 * Arrays and numbers arriving from the Go service can be `null` where these types say
 * array or number (Go marshals a nil slice as `null`). Normalise at the fetch boundary —
 * `apps/ui/src/lib/api.ts` `normalizeVerdict` is the precedent — never trust the type alone.
 */

// ---------------------------------------------------------------------------
// Closed enums (Appendix B; the Go validator is the source of truth)
// ---------------------------------------------------------------------------

/**
 * Closed enum mirroring the server's GlassAreas. No "other": every decision is rules-driven
 * from a laminated/tempered classification, so an unclassifiable area could only ever be
 * `unable_to_assess`. Adding glazing means editing this AND `ensemble.areaKind`.
 */
export type GlassArea =
  | 'FRONT_WINDSHIELD'
  | 'REAR_WINDSHIELD'
  | 'DRIVER_SIDE_FRONT_WINDOW'
  | 'PASSENGER_SIDE_FRONT_WINDOW'
  | 'DRIVER_SIDE_REAR_WINDOW'
  | 'PASSENGER_SIDE_REAR_WINDOW'
  | 'DRIVER_SIDE_QUARTER_GLASS'
  | 'PASSENGER_SIDE_QUARTER_GLASS'
  | 'ROOF_GLASS';

/** Every area, in the order you walk the car (UI-BRIEF §3). */
export const GLASS_AREAS: readonly GlassArea[] = [
  'FRONT_WINDSHIELD',
  'DRIVER_SIDE_FRONT_WINDOW',
  'PASSENGER_SIDE_FRONT_WINDOW',
  'DRIVER_SIDE_REAR_WINDOW',
  'PASSENGER_SIDE_REAR_WINDOW',
  'DRIVER_SIDE_QUARTER_GLASS',
  'PASSENGER_SIDE_QUARTER_GLASS',
  'ROOF_GLASS',
  'REAR_WINDSHIELD',
];

/**
 * Image roles. `vehicle_front` is the whole-vehicle identity photo: stored, never analysed
 * (`internal/api/types.go`). It was accepted by the server before either UI typed it.
 */
export type ImageRole =
  | 'glass_area'
  | 'vin_cutout'
  | 'logo_silkscreen'
  | 'scale_context'
  | 'vehicle_front';

/** Per-photo purpose for `glass_area` images (Link journey §2.2). */
export type ShotType = 'wide' | 'close' | 'angle' | 'scale';
export const SHOT_TYPES: readonly ShotType[] = ['wide', 'close', 'angle', 'scale'];

/** `original` is the upload; the rest are generated variants (8.2). */
export type ImageKind = 'original' | 'downscaled' | 'inverted' | 'contrast' | 'edge';
export const IMAGE_KINDS: readonly ImageKind[] = [
  'original',
  'downscaled',
  'inverted',
  'contrast',
  'edge',
];

/** `pending_review` sits between the decision and the terminal write (D19, Phase 16). */
export type JobStatus =
  | 'queued'
  | 'ingesting'
  | 'analysing'
  | 'enriching'
  | 'pending_review'
  | 'complete'
  | 'partial'
  | 'failed';

export type Decision =
  | 'repair'
  | 'replace'
  | 'no_damage'
  | 'insufficient_evidence'
  | 'unable_to_assess';

/**
 * Most severe first. This is the release-time fold's precedence
 * (`decision.Aggregate`, Appendix H 2026-09-22): the top-level decision is the most severe
 * per-area decision. The UI derives its "will release as" line from the same order.
 */
export const DECISION_PRECEDENCE: readonly Decision[] = [
  'unable_to_assess',
  'insufficient_evidence',
  'replace',
  'repair',
  'no_damage',
];

/**
 * What a REVIEWER may release, in the order they pick from (UI-BRIEF §3, R3).
 *
 * Four, not five. `unable_to_assess` is a machine-only, pre-review outcome: it means the
 * machine could not reach a defensible conclusion, and a person looking at the same
 * photographs either can — in which case they pick one of these — or cannot, in which case
 * `insufficient_evidence` closes the case (no retake is ever requested; Appendix H 2026-09-29). There is no
 * report for `unable_to_assess` (R2), so releasing one would hand an insurer a verdict with
 * no document behind it.
 *
 * The server refuses it too (21.1, 400), per area and in the aggregate. This list is what
 * stops a reviewer ever meeting that.
 */
export const DECISIONS: readonly Decision[] = [
  'repair',
  'replace',
  'no_damage',
  'insufficient_evidence',
];

/**
 * `automated` is the machine path; `human_verified` / `human_amended` are stamped by the
 * server at release (schema 1.3.0). The client never sends this.
 */
export type DecisionSource = 'automated' | 'human_verified' | 'human_amended';

/** Blocking-reason machine codes (`internal/decision/decision.go`, stable, part of B.4). */
export type BlockingReason =
  | 'missing_required_photos'
  | 'missing_damage_analysis'
  | 'insufficient_evidence'
  | 'low_confidence'
  | 'low_judge_agreement'
  | 'vin_mismatch'
  | 'operational_flags'
  | 'close_shot_unusable';

/** The closed list a reviewer may add from on `insufficient_evidence` / `unable_to_assess`. */
export const BLOCKING_REASONS: readonly BlockingReason[] = [
  'missing_required_photos',
  'missing_damage_analysis',
  'insufficient_evidence',
  'low_confidence',
  'low_judge_agreement',
  'vin_mismatch',
  'operational_flags',
  'close_shot_unusable',
];

// ---------------------------------------------------------------------------
// B.4 verdict (schema_version 1.3.0)
// ---------------------------------------------------------------------------

export type LegVerdict = 'damage' | 'no_damage' | 'unknown';
export type LegMode = 'voting' | 'shadow';

export interface LegSummary {
  /** Step key: `A_original`, `C_crosscheck`, `B_preprocessed`, … */
  leg: string;
  mode: LegMode;
  model: string;
  confidence?: number;
  latency_ms: number;
  /** The leg's own damage read. */
  verdict: LegVerdict;
  /** Present on newer payloads: the leg's per-area outcome word. */
  outcome?: string;
}

/** One damage finding inside `consensus.damagePoints[]` (prompt A.1/A.2 shape; loose). */
export interface DamagePoint {
  type?: string;
  estimatedLengthInches?: number | null;
  location?: string | null;
  inDPVA?: boolean | null;
  repairEligible?: boolean | null;
  description?: string | null;
  [key: string]: unknown;
}

export interface ScaleReference {
  kind?: string;
  present?: boolean;
  note?: string | null;
}

/** The agreed analysis for an area. Shape is prompt-defined and additive; read defensively. */
export interface Consensus {
  damageDetected?: boolean;
  damagePoints?: DamagePoint[];
  scaleReference?: ScaleReference;
  insufficiencyFlags?: string[];
  uncertaintyIndicators?: string[];
  evidenceSufficiencyAssessment?: string;
  overallConfidence?: number;
  glassArea?: string;
  [key: string]: unknown;
}

export interface AreaVerdict {
  glass_area: GlassArea;
  decision: Decision;
  /** Blocking machine codes for this area, when blocked. Wire may send `null`. */
  reasons?: string[];
  consensus?: Consensus;
  confidence: number;
  judge_agreement: number;
  legs: LegSummary[];
  /** 1.1.0: distinct voting steps behind this area. `1` means no corroboration. */
  voting_legs?: number;
  /** 1.2.0: the shot to re-capture first for this area, when the machine would ask. */
  retake_shot?: ShotType;
}

/** 1.2.0: one entry per photo a voting leg read. */
export interface ImageFeedback {
  job_image_id: string;
  glass_area?: GlassArea;
  shot_type?: ShotType;
  flags: string[];
  unreadable?: boolean;
  suggest_retake: boolean;
  source_uri?: string;
}

export type VinResultState = 'validated' | 'ocr_only' | 'insurer_only' | 'mismatch' | 'unavailable';

/** One reader's corrected opinion of the VIN (Appendix H 2026-10-27). */
export interface VinRead {
  source: 'vision' | 'gemini' | 'judge' | string;
  /** Absent when the reader produced nothing a VIN could be made of. */
  vin?: string;
  /** What the reader returned before I/O/Q correction, when that differs. */
  raw?: string;
  confidence: number;
}

export type VinResolutionMethod =
  | 'agreed'
  | 'single_vision'
  | 'single_gemini'
  | 'claim_match'
  | 'check_digit'
  | 'judged'
  | 'unresolved'
  | 'none';

/** How `ocr_reads` became `ocr_vin`. */
export interface VinResolution {
  method: VinResolutionMethod | string;
  vin?: string;
  confidence: number;
  /** 1-based positions the two readers disagreed on. */
  diff_positions?: number[];
  /** `judge_outside_candidates` when the adjudicator's answer was refused. */
  note?: string;
}

export interface VinEnrichment {
  vin_result_state: VinResultState | string;
  ocr_vin?: string;
  ocr_confidence?: number;
  /** Present only when the V_vin step is voting. */
  ocr_reads?: VinRead[];
  ocr_resolution?: VinResolution;
  vin_mismatch_flag?: boolean;
  vehicle?: { make: string; model: string; year?: number };
  adas_present?: boolean;
  adas_features?: string[];
}

export interface GlassTypeEnrichment {
  glassType: 'oem' | 'aftermarket' | 'unknown' | string;
  glassManufacturer?: string;
  confidence: number;
}

export interface Enrichment {
  vin?: VinEnrichment;
  glass_type?: GlassTypeEnrichment;
  warnings?: string[];
}

export interface Claim {
  policy_number?: string;
  customer_name?: string;
  journey_type: string;
  assessment_id?: string;
  vin?: string;
  /** Which side THIS vehicle's driver sits on. */
  handedness?: 'rhd' | 'lhd';
  /** Who submitted, for attribution (`link`, a console user, or `api`). */
  submitted_by?: string;
}

/**
 * The B.4 result body. `status` (1.1.0) is evidence completeness — `partial` means a model
 * failed and the verdict rests on less — and keeps that meaning after release.
 */
export interface Verdict {
  schema_version: string;
  job_id: string;
  claim: Claim;
  decision: Decision;
  decision_source: DecisionSource;
  justification: string;
  blocking_reasons: string[];
  rules_version: string;
  status?: 'complete' | 'partial';
  areas: AreaVerdict[];
  enrichment?: Enrichment;
  image_feedback?: ImageFeedback[];
  /** The machine's timestamp — unchanged by review. */
  generated_at: string;
}

/** The vehicle as a list row or the review queue shows it. */
export interface JobVehicle {
  make: string;
  model: string;
  year?: number;
}

/**
 * B.3: one row of `GET /v1/jobs`.
 *
 * 18.6 widened this so a list is ONE request rather than one per row. The four
 * added fields are what the field console's card draws:
 *
 * - `decision_source` is the "Reviewed" mark — `human_verified` or
 *   `human_amended` rather than `automated` (B.4 1.3.0). Absent until a verdict
 *   is released, because a held job does not have one.
 * - `held_at` is present only while the job waits for a reviewer, and drives
 *   the "In review" pill and its age.
 * - `vehicle` comes from the enrichment record rather than the verdict, so it
 *   is there in every state, including before any verdict exists.
 * - `claimed_areas` is the panes the operator claimed.
 */
export interface JobListItem {
  job_id: string;
  status: JobStatus;
  decision?: Decision;
  created_at: string;
  decision_source?: DecisionSource;
  held_at?: string;
  vehicle?: JobVehicle;
  claimed_areas?: GlassArea[];
}

/**
 * Per-stage turnaround for one job (migration `0024`, checklist item 10).
 *
 * Mirrors `store.StageTimestamps`. Every field is optional and **absent means UNKNOWN,
 * never zero** — a stage the job has not reached has no timestamp, and a consumer that
 * renders a missing stamp as a dash or a duration is inventing a measurement.
 *
 * Every stamp is write-once server side: D14 re-drives a stuck job through the same
 * transitions, so a last-write-wins clock would re-time recovered jobs from their final
 * attempt and the slowest jobs in the system would report the best numbers.
 *
 * `queued_at` is NOT a synonym for `created_at`: created_at is when the row appeared,
 * queued_at is when the job entered the queue. They coincide today, which is exactly why
 * they are separate — a consumer reading the wrong one still gets a plausible number.
 * Jobs written before 0024 have `queued_at` alone, backfilled from `created_at`.
 */
export interface StageTimestamps {
  queued_at?: string;
  ingesting_at?: string;
  analysing_at?: string;
  enriching_at?: string;
  held_at?: string;
  released_at?: string;
}

/** B.3.1: one row per image per kind, with a short-TTL signed GET URL. */
export interface JobImageOut {
  job_image_id: string;
  role: ImageRole;
  glass_area?: GlassArea;
  kind: ImageKind;
  url: string;
  expires_at: string;
  shot_type?: ShotType;
}

// ---------------------------------------------------------------------------
// B.7 review API — /v1/review/* (Entra reviewer only)
// ---------------------------------------------------------------------------

export interface ReviewMe {
  /** Entra object id — what `claimed_by` and `actor_oid` carry. */
  oid: string;
  upn: string;
  name: string;
  roles: string[];
}

export interface ReviewQueueItem {
  job_id: string;
  caller: string;
  held_at: string;
  age_sec: number;
  machine_decision: Decision;
  /** `machine_verdict.status`: complete | partial. */
  status: 'complete' | 'partial';
  /** Lowest `voting_legs` across the job's areas; `1` is the one-model caveat. */
  min_voting_legs?: number;
  /** Entra oid of the reviewer holding the soft lock, when any. */
  claimed_by?: string;
  claimed_until?: string;

  /**
   * The triage columns (UI-BRIEF §6.1): the insurer's claim number, the vehicle and the
   * assessed panes. Added to the queue reader on 2026-09-23 — none of it is new data, it
   * comes from the job's claim and the machine verdict the queue already reads. Optional
   * because each is best-effort server side: a claim with no policy number, a verdict whose
   * enrichment never decoded a vehicle. The UI falls back to a short job id.
   */
  policy_number?: string;
  vehicle?: { make: string; model: string; year?: number };
  areas?: GlassArea[];
}

export interface ReviewQueueResponse {
  jobs: ReviewQueueItem[];
  next_cursor?: string;
}

export type ReviewAction = 'approved' | 'amended';
export type ReviewState = 'pending' | 'released';

export interface ReviewInfo {
  state: ReviewState;
  held_at: string;
  claimed_by?: string;
  claimed_until?: string;
  action?: ReviewAction;
  reviewer_upn?: string;
  released_at?: string;
}

export type ReviewEventKind =
  | 'held'
  | 'opened'
  | 'claimed'
  | 'unclaimed'
  | 'field_changed'
  | 'released'
  | 'age_alert';

export interface ReviewEvent {
  event: ReviewEventKind;
  /** `'system'` for automated events. */
  actor_oid: string;
  actor_upn?: string;
  detail?: Record<string, unknown>;
  created_at: string;
}

/** The job row as the review reader returns it. */
export interface ReviewJob {
  job_id: string;
  status: JobStatus;
  caller: string;
  created_at: string;
  updated_at: string;
  claim?: Claim;
}

/**
 * A leg read row — one per image per step (`GetAreaLegs`). Includes shadow rows: reviewers
 * are internal and see every model's read.
 *
 * This is the shape the UI reads, AFTER `apps/review/src/api/normalize.ts`. It is not what
 * the wire carries: see `ReviewJobResponse.legs`.
 */
export interface ReviewLegRow {
  /** From the map KEY on the wire — the row itself carries no area. */
  glass_area: GlassArea;
  /** `leg` on the wire. */
  step_key: string;
  mode: LegMode;
  model: string;
  /** Derived: `parsed.overallConfidence`. The wire has no `confidence` field. */
  confidence?: number;
  latency_ms: number;
  /**
   * Derived: the wire has no `verdict`. A leg that returned damage points read damage, one
   * that returned none read no damage, and one that is `valid: false` — or that reports no
   * damage points at all, as the geometry leg does — read nothing we can call either way.
   */
  verdict: LegVerdict;
}

/**
 * One leg read AS THE API SENDS IT — `internal/api/review.go`'s `reviewLeg`, verbatim.
 *
 * The API is the source of truth for this shape and the types follow it, which is the
 * opposite of how it was: this file declared `ReviewLegRow[]` with `verdict`, `confidence`,
 * `glass_area` and `job_image_id`, and the wire has **none of those four**. The console
 * called `.filter` on an object and crashed on every real job, and the fields it would
 * have read next do not exist either.
 *
 * What is actually here: the step is `leg`; the area is the MAP KEY, not a field; the
 * leg's read lives inside `parsed`, which is the model's own output; and `valid` says
 * whether the leg returned anything usable at all.
 *
 * `normalize.ts` derives the rest — see `ReviewLegRow`.
 */
export interface ReviewLegRowWire {
  leg: string;
  mode: LegMode;
  model: string;
  latency_ms?: number | null;
  /** False when the leg failed: no usable read, whatever `parsed` holds. */
  valid?: boolean | null;
  /** The model's parsed output. `Consensus`-shaped in practice, but prompt-defined. */
  parsed?: Consensus | null;
}

export interface ReviewJobResponse {
  job: ReviewJob;
  /** The B.4 object as built by the machine — immutable, before any human touched it. */
  machine_verdict: Verdict;
  /**
   * The verdict that was actually sent, once the job has been released.
   *
   * The read-only released view shows the machine's verdict beside what the reviewer sent,
   * and those differ precisely when the review did something — `machine_verdict` alone
   * cannot show it. Carried by the job reader since 2026-09-23 (16.4.13). Optional because
   * a job that has not been released has no such verdict; the UI falls back to
   * `machine_verdict`, which is right for an approved job and understates an amended one.
   */
  released_verdict?: Verdict;
  enrichment?: Enrichment;
  images: JobImageOut[];
  /**
   * Leg reads, keyed by glass area — NOT an array.
   *
   * `apps/review/src/api/normalize.ts` flattens it into `ReviewLegRow[]` and carries the
   * area down from the key. Declaring the wire shape here, honestly, is what stops the
   * next screen writing `.filter` against it.
   */
  legs: Record<string, ReviewLegRowWire[] | null>;
  review: ReviewInfo;
  events: ReviewEvent[];
}

/**
 * The same job AFTER normalisation — what every screen consumes.
 *
 * Two types rather than one because there are genuinely two shapes: what the API sends and
 * what the UI can render. Collapsing them is what produced the crash — the single type
 * described the second while the wire carried the first, and nothing could tell them
 * apart. `normalizeJob` is the only function that turns one into the other.
 */
export interface ReviewJobDetail extends Omit<ReviewJobResponse, 'legs'> {
  legs: ReviewLegRow[];
}

export interface ClaimResponse {
  claimed_by: string;
  claimed_until: string;
}

export interface ReleaseArea {
  glass_area: GlassArea;
  decision: Decision;
  reasons: string[];
}

/**
 * `action` is intent only: the server derives approved/amended from its own diff and 400s
 * when the two disagree. Top-level `decision` and `blocking_reasons` are never sent.
 */
export interface ReleaseRequest {
  action: ReviewAction;
  areas: ReleaseArea[];
  justification: string;
  note?: string;
}

export interface ReleaseResponse {
  job_id: string;
  status: JobStatus;
  decision_source: DecisionSource;
  verdict: Verdict;
}

export interface HistoryItem {
  job_id: string;
  caller: string;
  released_at: string;
  reviewer_upn?: string;
  action: ReviewAction;
  machine_decision: Decision;
  released_decision: Decision;

  /**
   * The history table is read by claim number and vehicle, not by uuid — the same argument
   * that put them on the queue row. Carried by the history reader since 2026-09-23
   * (16.4.13), under the same names, so there is one shape. Optional for the same
   * best-effort reason as the queue row; the UI falls back to a short job id.
   */
  policy_number?: string;
  vehicle?: { make: string; model: string; year?: number };
}

export interface HistoryResponse {
  jobs: HistoryItem[];
  next_cursor?: string;
}

/** One address on the review-needed email list (Appendix H 2026-10-11). Never deleted. */
export interface NotifyRecipient {
  id: number;
  email: string;
  active: boolean;
  added_by: string;
  added_at: string;
  updated_by?: string;
  updated_at: string;
}

export interface NotifyRecipientsResponse {
  recipients: NotifyRecipient[];
  /** False when the deployment has no Graph settings: the list saves, nothing sends. */
  mail_enabled: boolean;
}

/** Stable error codes (B.6). */
export type ErrorCode =
  | 'ERR_UNAUTHENTICATED'
  | 'ERR_FORBIDDEN'
  | 'ERR_VALIDATION'
  | 'ERR_NOT_FOUND'
  | 'ERR_CONFLICT'
  | 'ERR_GCS_OBJECT_NOT_VISIBLE'
  | 'ERR_IMAGE_TOO_LARGE'
  | 'ERR_UNSUPPORTED_MEDIA_TYPE'
  | 'ERR_OVERLOADED'
  | 'ERR_UPSTREAM'
  | 'ERR_STUCK'
  | 'ERR_INTERNAL';

export interface ErrorEnvelope {
  error: { code: ErrorCode | string; message: string };
}

// ---------------------------------------------------------------------------
// Phase 21 — the assessment report
// ---------------------------------------------------------------------------

export { hasReport, REPORTABLE_DECISIONS, reportUrl, reviewReportUrl } from './report';
